import { MapContainer, TileLayer, Marker, Popup, Circle } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Kolory markerów wg statusu oferty
const MARKER_COLORS = {
  new: { fill: "#22d3ee", glow: "rgba(34,211,238,0.7)" },       // cyjan - nowa
  applied: { fill: "#34d399", glow: "rgba(52,211,153,0.7)" },   // zielony - aplikowano
  rejected: { fill: "#475569", glow: "rgba(71,85,105,0.4)" },   // stonowany - odrzucona
  archived: { fill: "#475569", glow: "rgba(71,85,105,0.3)" },   // stonowany - zarchiwizowana
};

function makeOfferIcon(offer) {
  const palette = MARKER_COLORS[offer.status] || MARKER_COLORS.new;
  const dimmed = offer.status === "rejected" || offer.status === "archived";
  const size = dimmed ? 12 : offer.matched ? 18 : 14;
  return L.divIcon({
    className: "custom-marker",
    html: `<div style="
      width:${size}px;height:${size}px;
      background:${palette.fill};
      border:2px solid #0b1120;
      border-radius:9999px;
      box-shadow:0 0 ${dimmed ? 6 : 14}px ${palette.glow};
      opacity:${dimmed ? 0.6 : 1};
    "></div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

const baseIcon = L.divIcon({
  className: "custom-marker",
  html: `<div style="width:18px;height:18px;background:#f59e0b;border:2px solid #0b1120;border-radius:9999px;box-shadow:0 0 16px rgba(245,158,11,0.6)"></div>`,
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

const STATUS_LABELS = {
  new: "Nowa",
  applied: "Aplikowano",
  rejected: "Odrzucona",
  archived: "Zarchiwizowana",
};

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("pl-PL", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso.slice(0, 10);
  }
}

function OfferPopupContent({ offer }) {
  const statusLabel = STATUS_LABELS[offer.status] || offer.status;
  return (
    <div className="offer-popup">
      <div className="offer-popup-header">
        <span className="offer-popup-title">{offer.title}</span>
        {offer.matched && <span className="offer-popup-badge offer-popup-badge-match">Dopasowana</span>}
      </div>
      <div className="offer-popup-school">{offer.school}</div>

      <div className="offer-popup-tags">
        {offer.level && <span className="offer-popup-tag">{offer.level}</span>}
        {offer.employment_type && <span className="offer-popup-tag">{offer.employment_type}</span>}
        {(offer.subjects || []).map((s) => (
          <span className="offer-popup-tag" key={s}>{s}</span>
        ))}
      </div>

      <div className="offer-popup-meta">
        {offer.distance_km != null && (
          <span className="offer-popup-meta-item offer-popup-meta-accent">{offer.distance_km} km</span>
        )}
        {offer.posted_date && (
          <span className="offer-popup-meta-item">Dodano: {formatDate(offer.posted_date)}</span>
        )}
        <span className="offer-popup-meta-item">{statusLabel}</span>
      </div>

      {offer.source_url && (
        <a href={offer.source_url} target="_blank" rel="noopener noreferrer" className="offer-popup-link">
          Zobacz ofertę →
        </a>
      )}
    </div>
  );
}

export default function OffersMap({ offers = [], profile }) {
  const center = profile?.lat && profile?.lng ? [profile.lat, profile.lng] : [52.2297, 21.0122]; // Warszawa fallback
  const radiusM = (profile?.max_radius_km || 30) * 1000;

  return (
    <div className="h-full w-full rounded-xl overflow-hidden border border-slate-800" data-testid="offers-map">
      <MapContainer center={center} zoom={9} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
        <TileLayer
          attribution='&copy; <a href="https://carto.com">CARTO</a> &copy; OpenStreetMap'
          url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
        />
        {profile?.lat && profile?.lng && (
          <>
            <Circle
              center={[profile.lat, profile.lng]}
              radius={radiusM}
              pathOptions={{ color: "#22d3ee", weight: 1, fillColor: "#22d3ee", fillOpacity: 0.05 }}
            />
            <Marker position={[profile.lat, profile.lng]} icon={baseIcon}>
              <Popup className="dark-popup">Twoja lokalizacja</Popup>
            </Marker>
          </>
        )}
        {offers
          .filter((o) => o.lat && o.lng)
          .map((o) => (
            <Marker key={o.id} position={[o.lat, o.lng]} icon={makeOfferIcon(o)}>
              <Popup className="dark-popup" minWidth={220}>
                <OfferPopupContent offer={o} />
              </Popup>
            </Marker>
          ))}
      </MapContainer>
    </div>
  );
}